import { useCallback, useMemo, useState } from 'react';
import {
	type ProjectType,
	SERVICE_DESK_PROJECT,
	PRODUCT_DISCOVERY_PROJECT,
	SOFTWARE_PROJECT,
} from '@atlassian/jira-common-constants/src/project-types';
import { CoordinationClient } from '@atlassian/jira-engagement';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import type { Environment } from '@atlassian/jira-shared-types';
import { useEnvironment, useTenantContext } from '@atlassian/jira-tenant-context-controller';

const getEPMessages = (
	environment: Environment,
	projectType: ProjectType,
): [string, string, string] => {
	const isProd = environment === 'prod';
	// [realtime message based on trait, realtime message targeting everyone, batch targeting everyone]
	let epMessageIds: [string, string, string] = ['', '', ''];

	if (projectType === SOFTWARE_PROJECT) {
		epMessageIds = isProd
			? ['jsw-upgrade-onboarding-modal', 'jsw-upgrade-onboarding', 'jsw-upgrade-onboarding-batch']
			: [
					'jsw-upgrade-onboarding-modal-stg',
					'jsw-upgrade-onboarding-stg',
					'jsw-upgrade-onboarding-batch-stg',
				];
	} else if (projectType === SERVICE_DESK_PROJECT) {
		epMessageIds = isProd
			? ['jsm-upgrade-onboarding-modal', 'jsm-upgrade-onboarding', 'jsm-upgrade-onboarding-batch']
			: [
					'jsm-upgrade-onboarding-modal-stg',
					'jsm-upgrade-onboarding-stg',
					'jsm-upgrade-onboarding-batch-stg',
				];
	} else if (projectType === PRODUCT_DISCOVERY_PROJECT) {
		epMessageIds = isProd
			? ['jpd-upgrade-onboarding-modal', 'jpd-upgrade-onboarding', 'jpd-upgrade-onboarding-batch']
			: [
					'jpd-upgrade-onboarding-modal-stg',
					'jpd-upgrade-onboarding-stg',
					'jpd-upgrade-onboarding-batch-stg',
				];
	}

	return epMessageIds;
};

export const useEPMessagesForCoordination = (projectType: ProjectType) => {
	const environment = useEnvironment();
	// null means coordination client has not yet determined what that message status is
	// true means the message has started and should be shown
	// false means the message should not be shown for any reason
	const [hasMessageStarted, setHasMessageStarted] = useState<null | boolean>(null);
	const { cloudId } = useTenantContext();
	const { data: orgId } = useOrgId();

	const coordinationClient = useMemo(
		() => new CoordinationClient(cloudId, '/gateway/api', false, orgId),
		[cloudId, orgId],
	);

	const [traitBasedMessage, realtimeMessage, batchMessage] = getEPMessages(
		environment,
		projectType,
	);

	const setEpMessageStarted = useCallback(async () => {
		// check ep message based on traits
		let started = await coordinationClient.start(traitBasedMessage);

		// check ep message based on realtime audience targeting everyone
		// this exists for new sites
		if (!started) {
			started = await coordinationClient.start(realtimeMessage);
		}

		// check ep message based on batch audience targeting everyone
		// this is for backfilled sites
		if (!started) {
			started = await coordinationClient.start(batchMessage);
		}
		setHasMessageStarted(started);
	}, [coordinationClient, traitBasedMessage, realtimeMessage, batchMessage]);

	const stopMessage = useCallback(async () => {
		await coordinationClient.stop(traitBasedMessage);
		await coordinationClient.stop(realtimeMessage);
		await coordinationClient.stop(batchMessage);
		setHasMessageStarted(false);
	}, [coordinationClient, traitBasedMessage, realtimeMessage, batchMessage]);

	return {
		hasMessageStarted,
		setEpMessageStarted,
		stopMessage,
	};
};

export default useEPMessagesForCoordination;
